import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { Modal } from "antd";
import { Trans } from "react-i18next";
import CommonButton from "@components/CommonButton";

interface NavigationGuardOptions {
  /** 用户点击确认离开时的回调，在路由跳转之前执行 */
  onConfirm?: () => void;
}

/**
 * 路由离开拦截守卫
 * 拦截 react-router 的 push/replace/go 以及浏览器刷新/关闭
 *
 * @param when - 是否启用拦截
 * @param options.onConfirm - 用户确认离开时的回调（清理数据等）
 * @returns
 *   - `showLeaveConfirm` 手动触发确认弹窗（如返回按钮）
 *   - `noGuardFn` 禁用拦截（如任务完成后允许自由跳转）
 *   - `GuardModal` 弹窗 JSX，放入 render 即可
 */
export function useNavigationGuard(when: boolean, options?: NavigationGuardOptions) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [routeBlocked, setRouteBlocked] = useState(false);
  const [manualBlocked, setManualBlocked] = useState(false);
  const pendingNavigation = useRef<(() => void) | null>(null);
  const noGuardRef = useRef(false);
  const onConfirmRef = useRef(options?.onConfirm);
  onConfirmRef.current = options?.onConfirm;

  useEffect(() => {
    if (!when) return;

    const { push, replace, go } = navigator;

    const block = (fn: () => void) => {
      if (noGuardRef.current) {
        fn();
        return;
      }
      setRouteBlocked(true);
      pendingNavigation.current = fn;
    };

    navigator.push = (...args: Parameters<typeof push>) => {
      block(() => {
        navigator.push = push;
        push.apply(navigator, args);
      });
    };

    navigator.replace = (...args: Parameters<typeof replace>) => {
      block(() => {
        navigator.replace = replace;
        replace.apply(navigator, args);
      });
    };

    navigator.go = (...args: Parameters<typeof go>) => {
      block(() => {
        navigator.go = go;
        go.apply(navigator, args);
      });
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (noGuardRef.current) return;
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when, navigator]);

  /** 手动触发确认弹窗（如点击返回按钮） */
  const showLeaveConfirm = useCallback(() => setManualBlocked(true), []);

  /** 禁用拦截（如任务完成后允许自由跳转） */
  const noGuardFn = useCallback(() => {
    noGuardRef.current = true;
  }, []);

  const isOpen = routeBlocked || manualBlocked;

  const handleConfirm = useCallback(() => {
    noGuardRef.current = true;
    onConfirmRef.current?.();
    if (routeBlocked) {
      pendingNavigation.current?.();
      pendingNavigation.current = null;
    }
    setRouteBlocked(false);
    setManualBlocked(false);
    noGuardRef.current = false;
  }, [routeBlocked]);

  const handleCancel = useCallback(() => {
    pendingNavigation.current = null;
    setRouteBlocked(false);
    setManualBlocked(false);
  }, []);

  const GuardModal = <NavigationGuardModal open={isOpen} onConfirm={handleConfirm} onCancel={handleCancel} />;

  return { showLeaveConfirm, noGuardFn, GuardModal };
}

function NavigationGuardModal({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;

  return (
    <Modal
      width={415}
      destroyOnHidden
      title={
        <div className="text-kds-fg font-bold text-[16px] font-[Inter]">
          <Trans>Leave this page?</Trans>
        </div>
      }
      open={open}
      centered
      onCancel={onCancel}
      footer={
        <div className="flex justify-end gap-[12px]">
          <CommonButton size="large" className="w-[145px]" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </CommonButton>
          <CommonButton size="large" className="w-[145px]" type="primary" onClick={onConfirm}>
            <Trans>Leave</Trans>
          </CommonButton>
        </div>
      }
      closable
    >
      <div className="text-kds-fg font-normal text-[14px] font-[Inter]">
        <Trans>The information you entered will not be saved.</Trans>
      </div>
    </Modal>
  );
}
